import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

const EJS_SERVICE = process.env.REACT_APP_EMAILJS_SERVICE_ID        || '';
const EJS_NOTIF   = process.env.REACT_APP_EMAILJS_NOTIF_TEMPLATE_ID || '';
const EJS_KEY     = process.env.REACT_APP_EMAILJS_PUBLIC_KEY         || '';

const STATUT_LABELS = {
  en_attente: 'En attente',
  complement: 'Complément requis',
  valide:     'Validée ✅',
  refuse:     'Refusée ❌',
  brouillon:  'Brouillon',
};

const STATUT_STYLE = {
  en_attente: { background:'#fff8e1', color:'#f57f17', border:'1px solid #ffe082' },
  complement: { background:'#fff3e0', color:'#e65100', border:'1px solid #ffb74d' },
  valide:     { background:'#e8f5e9', color:'#2e7d32', border:'1px solid #a5d6a7' },
  refuse:     { background:'#ffebee', color:'#c62828', border:'1px solid #ef9a9a' },
  brouillon:  { background:'#f5f5f5', color:'#777',    border:'1px solid #ddd' },
};

const STAT_COLORS = {
  red:    { border:'var(--fo-red)',    val:'#CC0000' },
  orange: { border:'var(--fo-orange)', val:'#c2410c' },
  green:  { border:'var(--fo-green)',  val:'#15803d' },
  blue:   { border:'var(--fo-blue)',   val:'#1d4ed8' },
};

function StatCard({ label, value, color = 'red', icon }) {
  const c = STAT_COLORS[color] || STAT_COLORS.red;
  return (
    <div style={{
      background:'#fff', borderRadius:10, padding:'16px 14px',
      border:'1px solid #e5e7eb', borderTop:`3px solid ${c.border}`,
      boxShadow:'0 1px 3px rgba(0,0,0,.05)', textAlign:'center',
    }}>
      <div style={{ fontSize:20, marginBottom:6, lineHeight:1 }}>{icon}</div>
      <div style={{ fontSize:26, fontWeight:800, color: c.val, lineHeight:1 }}>{value}</div>
      <div style={{ fontSize:11, color:'#9ca3af', marginTop:6, fontWeight:500 }}>{label}</div>
    </div>
  );
}

export default function DashboardDS({ demandes, currentUsername, submitComplementResponse, setPage, users }) {
  const [complementModal, setComplementModal] = useState(null);
  const [complementText, setComplementText] = useState('');
  const [complementDoc, setComplementDoc] = useState(null);
  const complementDocRef = useRef();

  const mesDemandes = (demandes || [])
    .filter(d => d.createdBy === currentUsername)
    .sort((a, b) => new Date(b.dateCreation) - new Date(a.dateCreation));

  const aComplementer = mesDemandes.filter(d => d.statut === 'complement');

  const stats = {
    total:     mesDemandes.length,
    en_attente: mesDemandes.filter(d => d.statut === 'en_attente').length,
    valide:    mesDemandes.filter(d => d.statut === 'valide').length,
    complement: aComplementer.length,
    refuse:    mesDemandes.filter(d => d.statut === 'refuse').length,
  };

  const handleSubmitComplement = () => {
    if (!complementModal || !complementText.trim()) return;
    submitComplementResponse(complementModal.id, complementText.trim(), complementDoc);
    if (EJS_SERVICE && EJS_NOTIF && EJS_KEY) {
      (users || []).filter(u => u.isAdmin && u.email).forEach(admin => {
        emailjs.send(EJS_SERVICE, EJS_NOTIF, {
          to_email: admin.email,
          destinataire_name: admin.username,
          sujet: `💬 Complément reçu — ${complementModal.prenom} ${complementModal.nom}`,
          message_body: `Le délégué ${currentUsername} a soumis le complément pour ${complementModal.prenom} ${complementModal.nom}.\n\nRéponse : ${complementText.trim()}\n\nConnectez-vous pour traiter cette demande.`,
        }, EJS_KEY).catch(() => {});
      });
    }
    setComplementModal(null);
    setComplementText('');
    setComplementDoc(null);
  };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>

      {/* ── Modal complément ── */}
      {complementModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:500 }}>
            <div className="modal-header">
              <span className="modal-title">Soumettre le complément</span>
              <button className="btn-icon" onClick={() => { setComplementModal(null); setComplementText(''); setComplementDoc(null); }}>✕</button>
            </div>
            <div style={{ background:'#f9fafb', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:14 }}>{complementModal.prenom} {complementModal.nom}</div>
              {complementModal.messageComplement && (
                <div style={{ fontSize:13, color:'#6b7280', marginTop:6 }}>
                  <strong>Demande :</strong> {complementModal.messageComplement}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Votre réponse</label>
              <textarea className="form-control" rows={4} value={complementText}
                onChange={e => setComplementText(e.target.value)}
                placeholder="Décrivez les informations complémentaires..." />
            </div>
            <div style={{ marginBottom:16 }}>
              <button className="btn btn-outline btn-sm" onClick={() => complementDocRef.current?.click()}>
                📎 {complementDoc ? `Fichier joint : ${complementDoc.nom}` : 'Joindre un document (optionnel)'}
              </button>
              {complementDoc && (
                <button className="btn btn-ghost btn-sm" style={{ color:'#b91c1c', marginLeft:6 }}
                  onClick={() => setComplementDoc(null)}>Retirer</button>
              )}
              <input ref={complementDocRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display:'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { alert('Fichier trop volumineux (max 5 Mo)'); e.target.value=''; return; }
                  const reader = new FileReader();
                  reader.onload = ev => setComplementDoc({ nom: file.name, type: file.type, data: ev.target.result });
                  reader.readAsDataURL(file);
                  e.target.value = '';
                }} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setComplementModal(null); setComplementText(''); setComplementDoc(null); }}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSubmitComplement} disabled={!complementText.trim()}>Envoyer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── En-tête ── */}
      <div className="page-header">
        <div>
          <div className="page-title">Mon espace</div>
          <div className="page-subtitle">{currentUsername} — Délégué(e) syndical(e)</div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary" onClick={() => setPage('adhesion')}>+ Nouvelle adhésion</button>
          <button className="btn btn-outline" onClick={() => setPage('recherche')}>Rechercher</button>
        </div>
      </div>

      {/* ── KPIs ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:24 }}>
        <StatCard icon="📋" label="Soumises"    value={stats.total}      color="red" />
        <StatCard icon="⏳" label="En attente"  value={stats.en_attente} color="orange" />
        <StatCard icon="✅" label="Validées"     value={stats.valide}     color="green" />
        <StatCard icon="💬" label="Compléments"  value={stats.complement} color="blue" />
      </div>

      {/* ── Compléments requis ── */}
      {aComplementer.length > 0 && (
        <div className="card mb-4" style={{ borderLeft:'4px solid #d97706', borderRadius:'0 10px 10px 0' }}>
          <div style={{ fontWeight:700, fontSize:14, color:'#92400e', marginBottom:12 }}>
            Action requise — {aComplementer.length} complément{aComplementer.length > 1 ? 's' : ''} attendu{aComplementer.length > 1 ? 's' : ''}
          </div>
          {aComplementer.map(d => (
            <div key={d.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, padding:'10px 0', borderBottom:'1px solid #fef3c7' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:14 }}>{d.prenom} {d.nom}</div>
                <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>
                  Soumis le {new Date(d.dateCreation).toLocaleDateString('fr-FR')}
                </div>
                {d.messageComplement && (
                  <div style={{ marginTop:6, fontSize:12, color:'#6b7280', fontStyle:'italic' }}>
                    "{d.messageComplement}"
                  </div>
                )}
              </div>
              <button className="btn btn-primary btn-sm" style={{ flexShrink:0 }}
                onClick={() => { setComplementModal(d); setComplementText(''); }}>
                Repondre
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Liste des demandes ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
        <span style={{ fontWeight:700, fontSize:14, color:'#374151' }}>Mes demandes</span>
        <span style={{ fontSize:12, color:'#9ca3af' }}>{mesDemandes.length} demande{mesDemandes.length > 1 ? 's' : ''}</span>
      </div>

      {mesDemandes.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-text">Aucune demande soumise pour l'instant.</div>
            <button className="btn btn-primary" style={{ marginTop:16 }} onClick={() => setPage('adhesion')}>
              + Soumettre une premiere adhesion
            </button>
          </div>
        </div>
      ) : (
        <div>
          {mesDemandes.map(d => {
            const style = STATUT_STYLE[d.statut] || STATUT_STYLE.brouillon;
            return (
              <div key={d.id} className="card mb-3" style={{ padding:'14px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:'#111' }}>{d.prenom} {d.nom}</div>
                    <div style={{ fontSize:12, color:'#9ca3af', marginTop:2 }}>
                      {d.metier || 'Metier non renseigne'} &middot; {new Date(d.dateCreation).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <span style={{ ...style, padding:'3px 11px', borderRadius:20, fontSize:12, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>
                    {STATUT_LABELS[d.statut] || d.statut}
                  </span>
                  {d.statut === 'complement' && (
                    <button className="btn btn-primary btn-sm"
                      onClick={() => { setComplementModal(d); setComplementText(''); }}>
                      Repondre
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
